import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Search, X, Archive, RotateCcw, Link2, Sprout } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import ModalPortal from "@/components/ui/modal-portal";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGION_LABEL = { reunion: "La Réunion", mayotte: "Mayotte" };
const FILTERS = [
  { id: "active", label: "Actives" },
  { id: "preciser", label: "À préciser" },
  { id: "archivee", label: "Archivées" },
];

async function api(path, options = {}) {
  const res = await authFetch(`${API_BASE}${path}`, { headers: { "Content-Type": "application/json" }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Action impossible");
  return data;
}

/**
 * Onglet admin « Cultures » : référentiel des cultures (MCD). Les cultures saisies en texte libre qui
 * ne correspondent à aucune culture apparaissent dans « À rattacher » : on les relie à une culture
 * existante (elles deviennent un synonyme) ou on crée une nouvelle culture.
 */
export default function CulturesPanel() {
  const [data, setData] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [filter, setFilter] = useState("active");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // culture ou {} pour une nouvelle
  const [attachTo, setAttachTo] = useState({});

  const load = useCallback(async () => {
    try {
      const [d, u] = await Promise.all([api("/admin/cultures"), api("/admin/cultures/a-rattacher")]);
      setData(d); setUnmatched(u);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => {
    if (!data) return [];
    const n = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    return data.cultures.filter((c) => {
      if (filter === "archivee" ? c.statut !== "archivee" : c.statut === "archivee") return false;
      if (filter === "preciser" && !c.a_preciser) return false;
      if (!q.trim()) return true;
      return [c.libelle, c.culture_id, c.code_oepp, ...(c.synonymes || []), ...(c.codes_rpg || [])].some((x) => n(x).includes(n(q)));
    });
  }, [data, filter, q]);

  const byFamily = useMemo(() => {
    const groups = {};
    for (const c of list) (groups[c.famille] = groups[c.famille] || []).push(c);
    return (data?.familles || []).filter((f) => groups[f]).map((f) => [f, groups[f]]);
  }, [list, data]);

  const attach = async (texte) => {
    const cid = attachTo[texte];
    if (!cid) { toast.error("Choisissez la culture à laquelle rattacher ce texte"); return; }
    try {
      const r = await api(`/admin/cultures/${cid}/rattacher`, { method: "POST", body: JSON.stringify({ texte }) });
      toast.success(`« ${texte} » rattaché à ${r.libelle}`); load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleArchive = async (c) => {
    try {
      await api(`/admin/cultures/${c.culture_id}/archive`, { method: "POST", body: JSON.stringify({ archive: c.statut !== "archivee" }) });
      toast.success(c.statut === "archivee" ? `${c.libelle} réactivée` : `${c.libelle} archivée`); load();
    } catch (e) { toast.error(e.message); }
  };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#2D6A4F" }} /></div>;
  const actives = data.cultures.filter((c) => c.statut !== "archivee");

  return (
    <div className="space-y-4" data-testid="cultures-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>Référentiel des cultures</h2>
          <p className="text-xs text-gray-500">
            {actives.length} cultures actives · {actives.filter((c) => c.code_oepp).length} avec code OEPP · {data.cultures.reduce((s, c) => s + c.parcelles, 0)} parcelles rattachées
          </p>
        </div>
        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => setEditing({})} data-testid="new-culture-btn">
          <Plus className="w-4 h-4 mr-1" />Nouvelle culture
        </Button>
      </div>

      {unmatched.length > 0 && (
        <section className="rounded-xl border p-3 space-y-2" style={{ background: "#FFF8E6", borderColor: "#F1D99A" }} data-testid="cultures-unmatched">
          <h3 className="text-sm font-semibold" style={{ color: "#7A5410" }}>À rattacher · {unmatched.length} culture{unmatched.length > 1 ? "s" : ""} saisie{unmatched.length > 1 ? "s" : ""} hors référentiel</h3>
          {unmatched.map((u) => (
            <div key={u.texte} className="flex flex-wrap items-center gap-2 bg-white rounded-lg border p-2 text-sm">
              <b>« {u.texte} »</b>
              <span className="text-xs text-gray-500">{u.occurrences} fois · {u.ou.join(", ")}</span>
              <div className="ml-auto flex gap-1.5 items-center">
                <select value={attachTo[u.texte] || ""} onChange={(e) => setAttachTo({ ...attachTo, [u.texte]: e.target.value })}
                  className="h-8 rounded-md border px-2 text-xs bg-white" aria-label={`Culture pour ${u.texte}`}>
                  <option value="">Rattacher à…</option>
                  {actives.map((c) => <option key={c.culture_id} value={c.culture_id}>{c.libelle}</option>)}
                </select>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => attach(u.texte)}><Link2 className="w-3.5 h-3.5 mr-1" />Rattacher</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditing({ libelle: u.texte })}>Créer la culture</Button>
              </div>
            </div>
          ))}
        </section>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg p-0.5 gap-0.5 bg-gray-100">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} className={`px-3 h-8 rounded-md text-xs font-semibold ${filter === f.id ? "bg-white shadow-sm text-emerald-800" : "text-gray-500"}`}>{f.label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-2 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Culture, synonyme, code RPG ou OEPP" className="h-8 pl-8 text-sm" data-testid="cultures-search" />
        </div>
      </div>

      {byFamily.length === 0 && <p className="text-sm text-gray-500 py-6 text-center">Aucune culture.</p>}
      {byFamily.map(([fam, items]) => (
        <section key={fam} className="space-y-1.5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">{fam} · {items.length}</h3>
          <div className="rounded-xl border bg-white divide-y overflow-hidden">
            {items.map((c) => (
              <div key={c.culture_id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 hover:bg-gray-50" data-testid={`culture-row-${c.culture_id}`}>
                <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: c.couleur_carte }} />
                <button onClick={() => setEditing(c)} className="text-sm font-semibold text-left hover:underline">{c.libelle}</button>
                {c.a_preciser && <span className="text-[10px] rounded px-1.5 py-0.5" style={{ background: "#FFF1D6", color: "#8A5300" }}>à préciser</span>}
                <span className="text-[11px] font-mono text-gray-500">{c.code_oepp || "—"}</span>
                <span className="text-[11px] text-gray-500">{(c.codes_rpg || []).join(" ") || ""}</span>
                <span className="text-[11px] text-gray-500 truncate max-w-[260px]">{(c.synonymes || []).length ? `aussi : ${c.synonymes.join(", ")}` : ""}</span>
                <div className="ml-auto flex items-center gap-3 shrink-0">
                <span className="text-[11px] text-gray-500 tabular-nums">{c.parcelles} parcelle{c.parcelles > 1 ? "s" : ""}</span>
                <span className="text-[11px] text-gray-500">{(c.regions || []).map((r) => (r === "reunion" ? "RE" : "YT")).join(" ")}</span>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => toggleArchive(c)} title={c.statut === "archivee" ? "Réactiver" : "Archiver"}>
                  {c.statut === "archivee" ? <RotateCcw className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {editing && <CultureForm culture={editing} familles={data.familles} groupesMfsc={data.groupes_mfsc || []} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function CultureForm({ culture, familles, groupesMfsc, onClose, onSaved }) {
  const isNew = !culture.culture_id;
  const [f, setF] = useState({
    libelle: culture.libelle || "", famille: culture.famille || "Autre", regions: culture.regions || ["reunion", "mayotte"],
    code_oepp: culture.code_oepp || "", espece_oepp: culture.espece_oepp || "", couleur_carte: culture.couleur_carte || "#7A7A7A",
    synonymes: culture.synonymes || [], codes_rpg: (culture.codes_rpg || []).join(" "), a_preciser: !!culture.a_preciser,
    groupes_mfsc: culture.groupes_mfsc || [],
  });
  const [syn, setSyn] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, codes_rpg: f.codes_rpg.split(/[\s,;]+/).filter(Boolean), code_oepp: f.code_oepp.trim(), espece_oepp: f.espece_oepp.trim() };
      if (!isNew && body.libelle === culture.libelle) delete body.libelle;
      await api(isNew ? "/admin/cultures" : `/admin/cultures/${culture.culture_id}`, { method: isNew ? "POST" : "PUT", body: JSON.stringify(body) });
      toast.success(isNew ? `Culture « ${f.libelle} » créée` : "Culture enregistrée");
      onSaved();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[2100] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" data-testid="culture-form">
        <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl shadow-2xl max-h-[92dvh] flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center gap-2">
            <Sprout className="w-4 h-4 text-emerald-700" />
            <div className="flex-1 font-bold text-sm">{isNew ? "Nouvelle culture" : culture.libelle}</div>
            {!isNew && <span className="text-[11px] font-mono text-gray-500">{culture.culture_id}</span>}
            <button onClick={onClose} aria-label="Fermer"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-4 space-y-3 overflow-y-auto text-sm">
            <label className="block space-y-1"><span className="text-xs text-gray-600">Libellé</span>
              <Input value={f.libelle} onChange={(e) => set("libelle", e.target.value)} data-testid="culture-libelle" /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1"><span className="text-xs text-gray-600">Famille</span>
                <select value={f.famille} onChange={(e) => set("famille", e.target.value)} className="h-9 w-full rounded-md border px-2 bg-white">
                  {familles.map((x) => <option key={x}>{x}</option>)}
                </select></label>
              <label className="block space-y-1"><span className="text-xs text-gray-600">Couleur sur la carte</span>
                <input type="color" value={f.couleur_carte} onChange={(e) => set("couleur_carte", e.target.value)} className="h-9 w-full rounded-md border" /></label>
            </div>
            <div className="space-y-1"><span className="text-xs text-gray-600">Régions</span>
              <div className="flex gap-4">{Object.entries(REGION_LABEL).map(([k, l]) => (
                <label key={k} className="flex items-center gap-1.5"><input type="checkbox" checked={f.regions.includes(k)}
                  onChange={(e) => set("regions", e.target.checked ? [...f.regions, k] : f.regions.filter((r) => r !== k))} />{l}</label>
              ))}</div></div>
            <div className="grid grid-cols-3 gap-3">
              <label className="block space-y-1"><span className="text-xs text-gray-600">Code OEPP</span>
                <Input value={f.code_oepp} onChange={(e) => set("code_oepp", e.target.value.toUpperCase())} className="font-mono" placeholder="ex. SACOF" /></label>
              <label className="block space-y-1 col-span-2"><span className="text-xs text-gray-600">Espèce (EPPO)</span>
                <Input value={f.espece_oepp} onChange={(e) => set("espece_oepp", e.target.value)} placeholder="ex. Saccharum officinarum" /></label>
            </div>
            <p className="text-[11px] text-gray-500 -mt-1">Vérifiez le code sur gd.eppo.int : il est repris dans l'export du registre phyto.</p>
            <label className="block space-y-1"><span className="text-xs text-gray-600">Codes RPG (séparés par des espaces)</span>
              <Input value={f.codes_rpg} onChange={(e) => set("codes_rpg", e.target.value.toUpperCase())} className="font-mono" placeholder="ex. CSA CAS" /></label>
            <div className="space-y-1"><span className="text-xs text-gray-600">Synonymes (autres orthographes reconnues)</span>
              <div className="flex flex-wrap gap-1.5">
                {f.synonymes.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs">{s}
                    <button onClick={() => set("synonymes", f.synonymes.filter((x) => x !== s))} aria-label={`Retirer ${s}`}><X className="w-3 h-3" /></button></span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={syn} onChange={(e) => setSyn(e.target.value)} placeholder="Ajouter un synonyme" className="h-8"
                  onKeyDown={(e) => { if (e.key === "Enter" && syn.trim()) { e.preventDefault(); set("synonymes", [...f.synonymes, syn.trim()]); setSyn(""); } }} />
                <Button size="sm" variant="outline" className="h-8" onClick={() => { if (syn.trim()) { set("synonymes", [...f.synonymes, syn.trim()]); setSyn(""); } }}>Ajouter</Button>
              </div></div>
            <div className="space-y-1"><span className="text-xs text-gray-600">Groupes E-Phy des matières fertilisantes (dans l'ordre)</span>
              <p className="text-[11px] text-gray-500">E-Phy donne les doses des engrais et biostimulants par grand groupe de cultures : le carnet propose la dose du premier groupe disponible pour le produit.</p>
              <div className="flex flex-wrap gap-1.5">
                {f.groupes_mfsc.map((g, i) => (
                  <span key={g} className="inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-900 px-2 py-0.5 text-xs">
                    <b className="tabular-nums">{i + 1}.</b> {g}
                    {i > 0 && <button type="button" onClick={() => { const a = [...f.groupes_mfsc]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; set("groupes_mfsc", a); }} aria-label={`Monter ${g}`} className="px-0.5">↑</button>}
                    <button type="button" onClick={() => set("groupes_mfsc", f.groupes_mfsc.filter((x) => x !== g))} aria-label={`Retirer ${g}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {!f.groupes_mfsc.length && <span className="text-xs text-gray-400">aucun : pas de dose E-Phy proposée</span>}
              </div>
              <select value="" onChange={(e) => e.target.value && set("groupes_mfsc", [...f.groupes_mfsc, e.target.value])}
                className="h-8 rounded-md border px-2 text-xs bg-white" aria-label="Ajouter un groupe E-Phy" data-testid="culture-groupe-add">
                <option value="">Ajouter un groupe…</option>
                {groupesMfsc.filter((g) => !f.groupes_mfsc.includes(g)).map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <label className="flex items-start gap-2"><input type="checkbox" checked={f.a_preciser} onChange={(e) => set("a_preciser", e.target.checked)} className="mt-1" />
              <span><b>À préciser par le titulaire</b><span className="block text-xs text-gray-500">Libellé trop général (ex. « Surface en herbe diverse ») : le titulaire indiquera sa culture réelle.</span></span></label>
          </div>
          <div className="border-t p-3 flex justify-end gap-2 bg-gray-50">
            <Button variant="ghost" size="sm" onClick={onClose}>Annuler</Button>
            <Button size="sm" onClick={save} disabled={saving || !f.libelle.trim()} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="culture-save">
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}{isNew ? "Créer" : "Enregistrer"}
            </Button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
